import axios from 'axios'
import qs from 'qs'
import { Message } from 'element-ui'
import apiEnv from '@/api/env'
import { getToken } from './auth'

function parseFileNameFromDisposition (contentDisposition) {
  if (!contentDisposition) return ''

  // 优先匹配 RFC 5987 格式：filename*=UTF-8''编码值
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch (error) {
      return utf8Match[1]
    }
  }

  // 匹配 filename="值" 或 filename=值
  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
  if (plainMatch && plainMatch[1]) {
    const raw = plainMatch[1].trim()
    try {
      return decodeURIComponent(raw)
    } catch (error) {
      return raw
    }
  }

  return ''
}

function triggerFileDownload (blob, fileName) {
  const downloadName = fileName || 'download'

  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, downloadName)
    return
  }

  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = downloadUrl
  link.download = downloadName
  link.style.display = 'none'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  window.setTimeout(() => {
    window.URL.revokeObjectURL(downloadUrl)
  }, 1000)
}

function looksLikeZipFile (blob) {
  return new Promise(resolve => {
    const reader = new FileReader()

    reader.onload = () => {
      const bytes = new Uint8Array(reader.result || new ArrayBuffer(0))
      resolve(bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b)
    }
    reader.onerror = () => resolve(false)
    reader.readAsArrayBuffer(blob.slice(0, 4))
  })
}

async function parseExportError (blob) {
  const text = await blob.text()

  if (!text) {
    return '导出失败，请稍后重试'
  }

  try {
    const json = JSON.parse(text)
    return json.msg || json.message || '导出失败，请稍后重试'
  } catch (error) {
    return text
  }
}

// 通用文件导出：默认 POST，支持 GET/FormData/自定义默认文件名，并优先从响应头解析真实文件名。
// 注意：需要后端 CORS 配置中暴露 Content-Disposition header，否则前端无法读取文件名。
export async function exportFile (config) {
  const method = String(config.method || 'post').toLowerCase()
  const responseType = config.responseType || 'blob'
  const defaultFileName = config.defaultFileName || config.fileName || 'download'
  const params = config.params || (method === 'get' ? config.data : undefined)
  const isFormData = config.data instanceof FormData
  const data = method === 'get'
    ? undefined
    : isFormData || typeof config.data === 'string'
      ? config.data
      : qs.stringify(config.data || {})

  const token = getToken()
  let response
  try {
    response = await axios({
      baseURL: config.baseURL || apiEnv.baseURL,
      method,
      timeout: config.timeout || 180000,
      responseType,
      url: config.url,
      data,
      params,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/x-www-form-urlencoded' }),
        ...(token ? { userreportpack: token } : {}),
        ...(config.headers || {})
      }
    })
  } catch (err) {
    // 后端返回 HTTP 500 等错误时，尝试从响应体中提取业务错误信息
    const resData = err.response && err.response.data
    let message = '导出失败，请稍后重试'
    if (resData instanceof Blob) {
      message = await parseExportError(resData)
    } else if (resData) {
      message = resData.msg || resData.message || message
    }
    return Promise.reject(new Error(message))
  }

  const responseBlob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' })
  const contentType = responseBlob.type || response.headers['content-type'] || ''
  const isZipFile = await looksLikeZipFile(responseBlob)

  // 解析文件名：后端通过独立 filename header 或 Content-Disposition header 返回
  let fileName = ''
  const rawFilename = response.headers['filename']
  if (rawFilename) {
    try {
      fileName = decodeURIComponent(rawFilename.replace(/\+/g, ' '))
    } catch (e) {
      fileName = rawFilename.replace(/\+/g, ' ')
    }
  }
  if (!fileName) {
    fileName = parseFileNameFromDisposition(response.headers['content-disposition'])
  }
  if (!fileName) {
    fileName = defaultFileName
  }
  triggerFileDownload(responseBlob, fileName)

  return {
    fileName,
    blob: responseBlob,
    headers: response.headers
  }
}
